import * as amplitude from '@amplitude/unified';
import * as Sentry from '@sentry/react';
import { AxiosError, HttpStatusCode } from 'axios';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import React, { createContext, useContext, useEffect } from 'react';
import { getAuthenticatedUser } from '@/api/user';
import { InitializingAppScreen } from '@/contexts/authContext/InitializingAppScreen';
import { useAccessCodeById } from '@/hooks/accessCode/useAccessCodeById';
import { preloadActivityGradingCriteria } from '@/hooks/activityGradingCriteria/useActivityGradingCriteria';
import { useFirebaseIdTokenChangedListener } from '@/hooks/auth/useFirebaseIdTokenChangedListener';
import { useLoginWithAccessCodeMutation } from '@/hooks/auth/useLoginWithAccessCodeMutation';
import { usePersistedAccessCodeId } from '@/hooks/auth/usePersistedAccessCodeId';
import { preloadSchoolSettingBySchoolId } from '@/hooks/school/useSchoolSettingBySchoolId';
import { useIsFirebaseAuthStateReady } from '@/hooks/useIsFirebaseAuthStateReady';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useAuthenticatedUser } from '@/hooks/user/useAuthenticatedUser';
import { AccessCode } from '@/model/AccessCode.model';
import { User } from '@/model/User.model';
import { clearCurrentSessionInfo } from '@/utils/activitySession';
import { AmplitudeHelper } from '@/utils/amplitudeHelper';

interface ContextType {
  user?: User | null;
  accessCode?: AccessCode | null;
  isLogin: boolean;
  isAuthenticating: boolean;
  loginWithAccessCode: (accessCode: string) => Promise<{ user: User; accessCode: AccessCode }>;
  loginWithEmailAndPassword: (email: string, password: string, remember?: boolean) => Promise<User>;
  loginWithGoogle: () => Promise<User>;
  logout: (options?: LogoutOptions) => Promise<void>;
  sendResetPasswordEmail(email: string, continueUrl?: string): Promise<void>;
}

// An options object rather than a positional argument: most logout buttons are wired as
// `onClick={logout}`, which would otherwise hand the click event straight to the redirect.
interface LogoutOptions {
  redirectTo?: string;
}

// Only same-origin app paths are honored — the value reaches `window.location`, and a
// caller-supplied absolute URL would turn logout into an open redirect.
const isSafeRedirectPath = (path: string) => path.startsWith('/') && !path.startsWith('//');

export const AuthContext = createContext<Partial<ContextType>>({});

export const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { trigger, isMutating: isLoggingIn, error: loginError } = useLoginWithAccessCodeMutation();
  const isFirebaseAuthStateReady = useIsFirebaseAuthStateReady();
  const [rememberMe, setRememberMe] = useLocalStorage('rememberMe', false);

  const logout = async (options?: LogoutOptions) => {
    const redirectTo = options?.redirectTo;
    AmplitudeHelper.trackUserLoggedOut();
    amplitude.reset(); // unlink future events from the current user
    await getAuth().signOut(); // firebase
    clearCurrentSessionInfo();
    setRememberMe(false); // don't leak a "remember me" choice into the next login on this device
    sessionStorage.clear(); // Treat logout as a new session / new tab
    // Full-page navigation (not a router push) so the redirect also clears cached app state.
    // Public pages such as enrollment pass their own path to stay in context instead of
    // bouncing the user out to the default home -> login route.
    window.location.href = redirectTo && isSafeRedirectPath(redirectTo) ? redirectTo : '/';
  };

  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useAuthenticatedUser({
    errorRetryCount: 1,
    errorRetryInterval: 2000,
    shouldRetryOnError: (error) => {
      if (error instanceof AxiosError && error.status === HttpStatusCode.Unauthorized) {
        return false;
      }
      return true;
    },
    onError: (error: AxiosError) => {
      if (error.status === HttpStatusCode.Unauthorized) {
        logout();
      }
    },
  });

  useEffect(() => {
    if (!user) {
      // preload calls only when user is logged in
      return;
    }
    preloadSchoolSettingBySchoolId(user.schoolId);
    preloadActivityGradingCriteria();
  }, [user]);

  useEffect(() => {
    if (isUserLoading) {
      return;
    }
    const scope = Sentry.getCurrentScope();
    if (user) {
      scope.setUser({ id: user.id });
      amplitude.setUserId(user.id);
      const identify = new amplitude.Identify();
      identify.set('user_role', user.role);
      identify.set('school_id', user.schoolId);
      amplitude.identify(identify);
    } else {
      scope.setUser(null);
    }
  }, [user, isUserLoading, userError]);

  const accessCodeId = usePersistedAccessCodeId(); // reads from local/session storage
  /**
   * Pass in falsy value to prevent querying accessCode until user is available, otherwise, the query will result in unauthorized error
   */
  const { data: accessCode } = useAccessCodeById(user ? accessCodeId : undefined, { revalidateOnFocus: false });

  useFirebaseIdTokenChangedListener(); // for login, logout, and token-refresh

  const isAuthenticating = isLoggingIn || isUserLoading;
  const isError = !!loginError || !!userError;
  const isLogin = !isAuthenticating && !isError && !!user;

  const loginWithAccessCode = async (accessCode: string) => {
    const result = await trigger(accessCode);
    return result;
  };

  // `remember` defaults to the shared checkbox-driven flag for callers with a remember-me
  // control (the enroll sign-in sheet, the main login page); callers without one — internal
  // admin login — pass `true` explicitly to keep their prior always-local-persistence behavior.
  const loginWithEmailAndPassword = async (email: string, password: string, remember: boolean = rememberMe) => {
    const auth = getAuth();
    setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
    await signInWithEmailAndPassword(auth, email, password);

    const authenticatedUser = await getAuthenticatedUser();
    return authenticatedUser;
  };

  const loginWithGoogle = async () => {
    const auth = getAuth();
    setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(auth, provider);

    const authenticatedUser = await getAuthenticatedUser();
    return authenticatedUser;
  };

  const sendResetPasswordEmail = async (email: string, continueUrl?: string) => {
    const auth = getAuth();
    const currentUrl = window.location.href;
    await sendPasswordResetEmail(auth, email, { url: continueUrl ?? currentUrl });
  };

  if (!isFirebaseAuthStateReady) {
    return <InitializingAppScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        user: user,
        accessCode: accessCode,
        isLogin: isLogin,
        isAuthenticating: isAuthenticating,
        loginWithAccessCode: loginWithAccessCode,
        loginWithEmailAndPassword,
        loginWithGoogle,
        logout: logout,
        sendResetPasswordEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
